import React from 'react';
import type { Table } from '../../types/@types/table';
import type { ServiceRequestItem } from '../../hooks/useServiceRequests';
import { Button } from '../ui';

interface TableCardProps {
  table: Table & { name?: string };
  onSelect: (table: Table) => void;
  onUnjoin?: (id: string) => void;
  isUnjoining?: boolean;
  serviceRequest?: ServiceRequestItem | null;
  onAttendService?: (id: number) => void;
}

const STATUS_LABELS: Record<Table['status'], string> = {
  Disponible: 'Disponible',
  Ocupada: 'Ocupada',
  Reservada: 'Reservada',
};

const STATUS_STYLES: Record<Table['status'], string> = {
  Disponible: 'bg-success text-white',
  Ocupada: 'bg-danger text-white',
  Reservada: 'bg-primary text-primary-fg',
};

export const TableCard = React.memo(function TableCard({
  table,
  onSelect,
  onUnjoin,
  isUnjoining,
  serviceRequest,
  onAttendService,
}: TableCardProps) {
  const isJoined = !!table.parent_id;

  return (
    <article className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-divider bg-surface p-4 shadow-card transition-shadow hover:shadow-card/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/70 focus-within:ring-offset-2 focus-within:ring-offset-surface">
      <div className="space-y-3">
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold leading-tight text-[clamp(18px,2.4vw,22px)] text-text">
              {table.name ?? `Mesa ${table.number}`}
            </p>
            <p className="text-sm font-medium text-text-muted">
              Mesa #{table.number}
            </p>
            {isJoined && (
              <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
                Unida a Mesa #{table.parent_id}
              </p>
            )}
            <p className="text-xs text-text-muted">ID: {table.id}</p>
          </div>
          <span
            className={`inline-flex min-h-[28px] items-center rounded-full px-3 text-xs font-semibold uppercase tracking-wide ${
              STATUS_STYLES[table.status]
            }`}
            aria-label={`Mesa ${table.number} ${STATUS_LABELS[table.status]}`}
          >
            {STATUS_LABELS[table.status]}
          </span>
        </header>

        {serviceRequest && serviceRequest.status === 'PENDING' && (
          <div className="flex items-center justify-between gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-rose-500 p-2.5 text-white shadow-sm animate-[pulse_2s_infinite]">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-lg">
                {serviceRequest.type === 'CALL_WAITER' ? '🛎️' : '💳'}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-black uppercase tracking-tight leading-tight truncate">
                  {serviceRequest.type === 'CALL_WAITER' ? '¡Llama Mesero!' : 'Pide Cuenta'}
                </p>
                <p className="text-[10px] text-white/90 truncate font-semibold">
                  {serviceRequest.type === 'REQUEST_BILL'
                    ? (serviceRequest.payment_method === 'CASH'
                        ? 'Efectivo 💵'
                        : serviceRequest.payment_method === 'CARD'
                        ? 'Datáfono 💳'
                        : 'Transferencia 📱')
                    : (serviceRequest.notes || 'Asistencia en mesa')}
                </p>
              </div>
            </div>
            {onAttendService && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAttendService(serviceRequest.id);
                }}
                className="shrink-0 rounded-lg bg-white/20 hover:bg-white/30 px-2 py-1 text-[11px] font-black text-white transition active:scale-95"
              >
                Atender ✓
              </button>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          onClick={() => onSelect(table)}
          className="min-h-[44px] w-full rounded-full text-[length:clamp(15px,2.4vw,17px)]"
          aria-label={`Seleccionar mesa ${table.number}`}
        >
          Seleccionar mesa
        </Button>
        {isJoined && onUnjoin && (
          <Button
            type="button"
            variant="outline"
            isLoading={isUnjoining}
            onClick={(e) => {
              e.stopPropagation();
              onUnjoin(table.id);
            }}
            className="min-h-[44px] w-full rounded-full text-xs"
          >
            Separar Mesa
          </Button>
        )}
      </div>
    </article>
  );
});

export default TableCard;
